/**
 * Imports (Settings → Import; owners and admins): bring a Slack workspace's conversations or a Notion workspace's
 * pages and databases into this workspace.
 *
 * Sources, each either an export file or a token:
 * - **Slack**: the admin export .zip (Workspace settings → Import/Export data; public channels on Free/Pro, private
 *   channels and DMs too on Business+/Enterprise exports) uploaded as a file (files.ts, `purpose: "import"`), or a
 *   Slack **user token** (`xoxp-…`) of an app made in that Slack workspace (manifest in `SLACK_IMPORT_MANIFEST`),
 *   which reads every conversation that person can see, on any plan.
 * - **Notion**: the "Markdown & CSV" export .zip (Settings → Export all workspace content; subpages included), or the
 *   secret of an internal Notion integration (`ntn_…` / `secret_…`) that the pages were shared with.
 *
 * Flow (an `ImportJob`): create → `scanning` (the server reads the source: people and channels for Slack; the page
 * tree for Notion, then a model proposes how to sort it) → `review` (the admin matches Slack people to members and
 * picks channels; accepts or edits the proposed Notion structure) → start → `importing` (progress) → `done` | `failed`.
 * Cancel works until it's done. Imported messages keep their original time and threads but notify nobody; people not
 * matched to a member keep their name and picture on their messages (Slack's "import as name only"). Tokens are kept
 * only while the job runs, except a Slack token import's, handed to the hourly sync (`SlackSyncSettings`) until the admin
 * finishes the move. Clients poll the job while it is scanning or importing, and show its timeline.
 */
import { z } from "zod";
import { PageVisibility, ViewLayout } from "./docs.ts";
import { ChannelKind, Id, Timestamp } from "./entities.ts";

export const ImportSource = z.enum(["slack", "notion", "googleDrive"]);
export type ImportSource = z.infer<typeof ImportSource>;

/**
 * `file`: an uploaded export .zip; `token`: read through the source's API; `google`: the workspace's Google Workspace
 * connection (mailSync.ts, domain-wide delegation).
 */
export const ImportVia = z.enum(["file", "token", "google"]);
export type ImportVia = z.infer<typeof ImportVia>;

export const ImportStatus = z.enum(["scanning", "review", "importing", "done", "failed", "cancelled"]);
export type ImportStatus = z.infer<typeof ImportStatus>;

/** Largest export accepted (same cap as any upload). Notion splits bigger workspaces into several parts. */
export const IMPORT_MAX_BYTES = 1_073_741_824;

export const CreateImportRequest = z
  .object({
    source: ImportSource,
    /** A ready upload made with `purpose: "import"` (the export .zip). */
    fileId: Id.optional(),
    /** Slack user token or Notion integration secret. */
    token: z.string().min(10).max(500).optional(),
    /**
     * Slack with a token only: no new import — fetch again the files this workspace's messages still link to on Slack
     * (copy them, make canvases pages) and replace the links. The job starts at once (no review).
     */
    repairLinks: z.boolean().optional(),
  })
  .refine((r) => r.source === "googleDrive" ? r.fileId === undefined && r.token === undefined : (r.fileId === undefined) !== (r.token === undefined), {
    message: "Give either fileId or token (Google Drive: neither, it reads through the Google Workspace connection)",
  })
  .refine((r) => !r.repairLinks || (r.source === "slack" && r.token !== undefined), { message: "repairLinks needs a Slack token" });
export type CreateImportRequest = z.infer<typeof CreateImportRequest>;

// ---------- Slack ----------

/**
 * What happens to one Slack person (Slack's importer offers the same): their messages go to an existing `member`;
 * `invite` creates their account now and emails an invite (their history is theirs when they join); `guest` does the
 * same as an external guest (restricted to the channels they were in, "External · org" badge, sponsored by the person
 * importing); `deactivated` keeps a deactivated profile (people who left); `name` shows only their name and picture.
 */
export const SlackPersonAction = z.enum(["member", "invite", "guest", "deactivated", "name"]);
export type SlackPersonAction = z.infer<typeof SlackPersonAction>;

export const SlackPersonChoice = z.object({ action: SlackPersonAction, userId: Id.optional() });
export type SlackPersonChoice = z.infer<typeof SlackPersonChoice>;

export const SlackImportUser = z.object({
  slackId: z.string(),
  /** Display name, else the handle. */
  name: z.string(),
  /** Slack handle (`name`; group DMs are named after handles). */
  handle: z.string().optional(),
  realName: z.string().optional(),
  email: z.string().optional(),
  avatarUrl: z.string().optional(),
  bot: z.boolean(),
  deleted: z.boolean(),
  /** Member with the same email, matched automatically (the admin can change it). */
  matchUserId: Id.optional(),
  /** A Slack guest (multi- or single-channel). */
  guest: z.boolean().optional(),
  /** Another company: their email domain isn't the workspace's (or they're a Slack Connect / guest account). */
  external: z.object({ name: z.string(), domain: z.string().optional() }).optional(),
  /** What the review starts with: member when matched, invite / guest when they have an email, deactivated, or name. */
  suggested: SlackPersonAction,
  /** Messages written by this person in the source (a count, or a lower bound for token imports). */
  messageCount: z.number().int().nonnegative().optional(),
});
export type SlackImportUser = z.infer<typeof SlackImportUser>;

export const SlackImportChannel = z.object({
  slackId: z.string(),
  /** Channel name; DMs: the people's names. */
  name: z.string(),
  kind: ChannelKind,
  archived: z.boolean(),
  topic: z.string().optional(),
  purpose: z.string().optional(),
  memberSlackIds: z.array(z.string()),
  /** Known for exports; absent for token imports until imported. */
  messageCount: z.number().int().nonnegative().optional(),
  firstAt: Timestamp.optional(),
  lastAt: Timestamp.optional(),
  /** A channel of this workspace with the same name: messages are added to it unless the admin renames. */
  existingChannelId: Id.optional(),
  /**
   * A DM with an app (Notion, Google Drive, Slackbot…): its name. Off by default; when picked it comes over as a
   * private channel for the person (`name`, e.g. "notion-dm"), the app's messages shown under the app's name.
   */
  app: z.string().optional(),
});
export type SlackImportChannel = z.infer<typeof SlackImportChannel>;

export const SlackImportPreview = z.object({
  workspaceName: z.string().optional(),
  users: z.array(SlackImportUser),
  channels: z.array(SlackImportChannel),
  /** e.g. `public_only`: the export has no private channels or DMs (a Free/Pro export). */
  warnings: z.array(z.string()),
});
export type SlackImportPreview = z.infer<typeof SlackImportPreview>;

export const SlackImportChoice = z.object({
  slackId: z.string(),
  include: z.boolean(),
  /** New name for a public/private channel (lowercase, as CreateChannelRequest). */
  name: z.string().min(1).max(80).optional(),
});
export type SlackImportChoice = z.infer<typeof SlackImportChoice>;

export const SlackImportOptions = z.object({
  /** Slack user id → what to do with them (`member` needs `userId`). Missing ids use `suggested`. */
  people: z.record(z.string(), SlackPersonChoice),
  /** Channels to import; missing ones are imported when they are channels, skipped when DMs aren't listed. */
  channels: z.array(SlackImportChoice),
  /** Download attached files into Bundle (default true); otherwise messages link to the Slack originals. */
  files: z.boolean().optional(),
  /**
   * Email the invites now (default true). Off: accounts, profiles and pending invites are still created (their history
   * is theirs), but nobody is emailed until an admin resends from Invitations.
   */
  sendInvites: z.boolean().optional(),
  /**
   * People from another company only get the channels they were in (default true): invited as restricted guests of
   * their company, and their deactivated profiles restricted too. Off: they're set up like everyone else.
   */
  restrictExternal: z.boolean().optional(),
});
export type SlackImportOptions = z.infer<typeof SlackImportOptions>;

// ---------- Notion ----------

export const NotionImportNode = z.object({
  /** Stable within the job (the Notion id). */
  key: z.string(),
  parentKey: z.string().nullable(),
  title: z.string(),
  kind: z.enum(["page", "database"]),
  icon: z.string().optional(),
  /** Databases. */
  rowCount: z.number().int().nonnegative().optional(),
  columns: z.array(z.string()).optional(),
  /** Pages: has a body. */
  hasContent: z.boolean().optional(),
});
export type NotionImportNode = z.infer<typeof NotionImportNode>;

/** A top-level group of the sorted structure: a page the imported pages go under. */
export const NotionImportSection = z.object({
  id: z.string(),
  title: z.string().min(1).max(200),
  icon: z.string().max(64).optional(),
  visibility: PageVisibility,
  /** Top-level nodes (keys) placed under this section, in order. Their subtrees come along. */
  nodeKeys: z.array(z.string()),
});
export type NotionImportSection = z.infer<typeof NotionImportSection>;

export const NotionImportDatabaseHint = z.object({
  key: z.string(),
  /** First view of the database (e.g. a task list → board). */
  layout: ViewLayout,
  /** Column the board groups by (a status/select column), or the calendar's date column. */
  column: z.string().optional(),
});
export type NotionImportDatabaseHint = z.infer<typeof NotionImportDatabaseHint>;

/**
 * How the pages will be arranged. Proposed by a model from titles, paths, short snippets and database columns
 * (`ai: "used"`); without one, or when it fails, every top-level page stays where it was (`unavailable` / `failed`).
 */
export const NotionImportPlan = z.object({
  sections: z.array(NotionImportSection),
  databases: z.array(NotionImportDatabaseHint),
  /** Clearer titles for untitled or cryptic pages (key → title). */
  titles: z.record(z.string(), z.string().min(1).max(200)),
  /** One or two sentences on how it was sorted, for the review screen. */
  summary: z.string().max(1000).optional(),
  ai: z.enum(["used", "unavailable", "failed"]),
  /** Top-level pages / databases (keys) left out of the import, with everything under them. */
  excluded: z.array(z.string()).optional(),
});
export type NotionImportPlan = z.infer<typeof NotionImportPlan>;

export const NotionImportPreview = z.object({
  /** The Notion workspace the token belongs to (token imports). */
  workspaceName: z.string().optional(),
  nodes: z.array(NotionImportNode),
  plan: NotionImportPlan,
  warnings: z.array(z.string()),
});
export type NotionImportPreview = z.infer<typeof NotionImportPreview>;

export const NotionImportOptions = z.object({ plan: NotionImportPlan });
export type NotionImportOptions = z.infer<typeof NotionImportOptions>;

// ---------- Google Drive ----------
//
// docs/features/google-drive-import.md. Reads every chosen person's My Drive (files they own) and every chosen shared
// drive through the workspace's Google Workspace connection (scope GOOGLE_DRIVE_SCOPE on its domain-wide delegation).
// Conventions (Google Takeout / Drive → Dropbox migrations): Docs → .docx, Sheets → .xlsx, Slides → .pptx,
// Drawings → .pdf; Forms, Sites, Maps, Apps Script… aren't copied (listed as problems with their Google link); a
// person's My Drive lands in a "Google Drive" folder in their My files; a shared drive becomes a top-level folder owned
// by the admin who ran the import and shared like the drive; Google shortcuts become Files shortcuts.

/** The Drive scope the connection's delegation needs (read-only). */
export const GOOGLE_DRIVE_SCOPE = "https://www.googleapis.com/auth/drive.readonly";
export const GOOGLE_DRIVE_PROBLEMS_MAX = 500;

/** One Google user of the connection, with the Bundle member their files go to (none: not in Bundle, can't import). */
export const GoogleDrivePerson = z.object({
  email: z.string(),
  name: z.string(),
  photoUrl: z.string().optional(),
  memberId: Id.optional(),
  /** Bytes their My Drive uses (Google's quota figure; absent when Google wouldn't say). */
  bytes: z.number().int().nonnegative().optional(),
  /** Why their Drive can't be read (suspended, Drive turned off for them…). */
  error: z.string().optional(),
});
export type GoogleDrivePerson = z.infer<typeof GoogleDrivePerson>;

export const GoogleDriveSharedDrive = z.object({
  id: z.string(),
  name: z.string(),
  /** People with access (any role). */
  members: z.number().int().nonnegative(),
  /** An organizer the drive is read as (absent: no member of the Google Workspace can read it). */
  readAs: z.string().optional(),
  error: z.string().optional(),
});
export type GoogleDriveSharedDrive = z.infer<typeof GoogleDriveSharedDrive>;

export const GoogleDrivePreview = z.object({
  /** The Google Workspace connection read through (mailSync.ts). */
  connectionId: Id,
  adminEmail: z.string(),
  people: z.array(GoogleDrivePerson),
  sharedDrives: z.array(GoogleDriveSharedDrive),
  /** Files storage left in this workspace (bytes), to warn when the import won't fit. */
  storageLeft: z.number().int().nonnegative(),
  /** An earlier Google Drive import of this workspace: running it again brings only new and changed files. */
  previousJobId: Id.optional(),
});
export type GoogleDrivePreview = z.infer<typeof GoogleDrivePreview>;

/** What the admin chose: people by Google email (only people with a member), shared drives by id. */
export const GoogleDriveImportOptions = z.object({
  people: z.array(z.string()).max(5000),
  sharedDrives: z.array(z.string()).max(5000),
});
export type GoogleDriveImportOptions = z.infer<typeof GoogleDriveImportOptions>;

/** `attention`: Google refused to read it (the import goes on with the others). */
export const GoogleDriveUnitStatus = z.enum(["waiting", "listing", "copying", "done", "attention"]);
export type GoogleDriveUnitStatus = z.infer<typeof GoogleDriveUnitStatus>;

export const GoogleDriveUnit = z.object({
  /** `p:<email>` or `d:<shared drive id>`. */
  key: z.string(),
  kind: z.enum(["person", "sharedDrive"]),
  /** The person's name or the shared drive's name. */
  label: z.string(),
  status: GoogleDriveUnitStatus,
  /** Items (files and folders) done, of `total` once listed. */
  done: z.number().int().nonnegative(),
  total: z.number().int().nonnegative().optional(),
  bytes: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  error: z.string().optional(),
  /** The Files folder it lands in (a person's "Google Drive", a shared drive's top-level folder), once made. */
  rootItemId: Id.optional(),
});
export type GoogleDriveUnit = z.infer<typeof GoogleDriveUnit>;

/**
 * A file that didn't come over: `tooBig` (over Files' limit or Google's export limit), `notCopyable` (Forms, Sites,
 * Maps…: Google has no file to export), `exportFailed`, `refused` (Google wouldn't hand it over), `gone` (deleted in Google
 * before it was copied), `shortcutTarget` (a shortcut whose target isn't in the import), `folderMissing` (its folder
 * didn't come over), `storageFull` (Files ran out of room: the import stopped copying), `failed`.
 */
export const GoogleDriveProblem = z.object({
  unit: z.string(),
  name: z.string(),
  path: z.string(),
  reason: z.enum(["tooBig", "notCopyable", "exportFailed", "refused", "gone", "shortcutTarget", "folderMissing", "storageFull", "failed"]),
  /** Open it in Google. */
  link: z.string().optional(),
  at: Timestamp,
});
export type GoogleDriveProblem = z.infer<typeof GoogleDriveProblem>;

// ---------- Jobs ----------

export const StartImportRequest = z.object({
  slack: SlackImportOptions.optional(),
  notion: NotionImportOptions.optional(),
  googleDrive: GoogleDriveImportOptions.optional(),
});
export type StartImportRequest = z.infer<typeof StartImportRequest>;

export const ImportPhase = z.enum(["reading", "sorting", "channels", "messages", "files", "pages", "finishing", "listing"]);
export type ImportPhase = z.infer<typeof ImportPhase>;

export const ImportProgress = z.object({
  phase: ImportPhase,
  done: z.number().int().nonnegative(),
  /** Absent while unknown. */
  total: z.number().int().nonnegative().optional(),
});
export type ImportProgress = z.infer<typeof ImportProgress>;

export const ImportResult = z.object({
  channels: z.number().int().nonnegative(),
  messages: z.number().int().nonnegative(),
  pages: z.number().int().nonnegative(),
  databases: z.number().int().nonnegative(),
  rows: z.number().int().nonnegative(),
  files: z.number().int().nonnegative(),
  /** Items that could not be brought over (unsupported content, files that failed to download…). */
  skipped: z.number().int().nonnegative(),
  /** Google Drive: folders made, bytes copied, shared drives brought over. */
  folders: z.number().int().nonnegative().optional(),
  bytes: z.number().int().nonnegative().optional(),
  sharedDrives: z.number().int().nonnegative().optional(),
});
export type ImportResult = z.infer<typeof ImportResult>;

/**
 * What a job did, step by step (newest last; the oldest are dropped past IMPORT_TIMELINE_MAX). Clients render
 * `import.timeline.<code>` with `params`, so the server writes no prose.
 */
export const ImportTimelineCode = z.enum([
  "started", // { source }
  "people", // { count }
  "invited", // { count } accounts created and invited as members
  "guests", // { count } external guests invited
  "deactivatedPeople", // { count } deactivated profiles kept
  "conversations", // { channels, dms }
  "pages", // { pages, databases }
  "sorting",
  "sorted", // { count } sections
  "sortSkipped",
  "review",
  "importing",
  "channel", // { name } created (or found) in Bundle
  "history", // { name, count } messages read from Slack so far
  "files", // { name, count } files copied from that conversation so far
  "channelDone", // { name, count } messages written
  "page", // { title }
  "database", // { title }
  "rows", // { title, count } rows written so far
  "canvas", // { title } a Slack canvas / post made a page
  "filesRepaired", // { count } messages whose Slack file links were replaced at the end
  "repairing", // { count } messages found still linking to Slack files (repairLinks jobs)
  "renamed", // { count } profiles set up with the Slack @handle as name now show the name Slack shows
  "fileNotFound", // { name } a linked Slack file: Slack has no such file any more (deleted, or not visible to this token)
  "fileNoAccess", // { name } Slack wouldn't hand the file over (hidden by the plan's limits, or an external file)
  "fileTooBig", // { name } over Bundle's upload limit
  "canvasUnreadable", // { name } a canvas / post whose content Slack didn't give as a page
  "fileFailed", // { name } the download or the upload failed
  "driveUnit", // { name } a Google person's My Drive or a shared drive started (listing)
  "driveUnitDone", // { name, files, folders } that unit finished
  "driveUnitAttention", // { name, message } that unit stopped: Google refused (suspended user, delegation revoked…)
  "driveShortcuts", // { count } Google shortcuts made Files shortcuts at the end
  "rateLimited", // { seconds }
  "retrying", // { attempt, message }
  "done",
  "failed", // { message }
  "cancelled",
]);
export type ImportTimelineCode = z.infer<typeof ImportTimelineCode>;

export const IMPORT_TIMELINE_MAX = 300;

export const ImportTimelineEntry = z.object({
  at: Timestamp,
  code: ImportTimelineCode,
  params: z.record(z.string(), z.union([z.string(), z.number()])).optional(),
});
export type ImportTimelineEntry = z.infer<typeof ImportTimelineEntry>;

/**
 * Undoing a Notion import (owners and admins), Notion's trash rule: "Move to trash" first (restorable from Trash, each
 * top-level item with everything under it), then "Delete permanently" from the trash. Counts: what the job created that
 * is still in the workspace (trash) or in the trash (delete) — pages and databases, with their rows and attached files.
 * `basis`: `map` — the ids the job recorded; `window` — jobs from before ids were kept: pages the importer created
 * while the job ran. `kept`: items left alone because someone else created or edited something in them since.
 */
export const ImportTrashCounts = z.object({
  pages: z.number().int().nonnegative(),
  databases: z.number().int().nonnegative(),
  rows: z.number().int().nonnegative(),
  files: z.number().int().nonnegative(),
  /** Google Drive imports: Files folders (files counts Files items that aren't folders). */
  folders: z.number().int().nonnegative().optional(),
  kept: z.number().int().nonnegative(),
  basis: z.enum(["map", "window"]),
});
export type ImportTrashCounts = z.infer<typeof ImportTrashCounts>;

/**
 * The last undo run on a job (a Workflow, in batches): `trash` or `delete`, `running` → `done` | `failed`. `done` of
 * `total` pages and databases so far; `counts` is what the run set out to move or delete.
 */
export const ImportUndo = z.object({
  action: z.enum(["trash", "delete"]),
  status: z.enum(["running", "done", "failed"]),
  done: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  counts: ImportTrashCounts,
  updatedAt: Timestamp,
});
export type ImportUndo = z.infer<typeof ImportUndo>;

export const ImportJob = z.object({
  id: Id,
  source: ImportSource,
  via: ImportVia,
  status: ImportStatus,
  /** The export's file name, or the source workspace's name. */
  label: z.string(),
  createdBy: Id,
  createdAt: Timestamp,
  updatedAt: Timestamp,
  progress: ImportProgress.optional(),
  result: ImportResult.optional(),
  /** `failed`: why (e.g. `invalid_token`, `not_a_slack_export`, `not_a_notion_export`). */
  error: z.object({ code: z.string(), message: z.string() }).optional(),
  /** `review` and later (GET one job only; lists leave them out). */
  slack: SlackImportPreview.optional(),
  notion: NotionImportPreview.optional(),
  googleDrive: GoogleDrivePreview.optional(),
  /** Google Drive, while importing and after (GET one job only): each person's My Drive and each shared drive. */
  driveUnits: z.array(GoogleDriveUnit).optional(),
  /** Google Drive (GET one job only): what didn't come over, newest last (at most GOOGLE_DRIVE_PROBLEMS_MAX). */
  driveProblems: z.array(GoogleDriveProblem).optional(),
  /** What the admin chose on start. */
  options: StartImportRequest.optional(),
  /** A repairLinks job: it only fetched again files left as Slack links. */
  repair: z.boolean().optional(),
  /**
   * A person's own import (docs/features/imports.md): their Slack DMs, group DMs and private channels, read with their
   * own user token from Slack's OAuth. Only its creator sees and runs it; nobody is invited or created.
   */
  personal: z.boolean().optional(),
  /** Where the imported pages went (Notion): the section pages, to open them. */
  rootPageIds: z.array(Id).optional(),
  /** Google Drive: the Files folders the import made at the top (each person's "Google Drive", each shared drive's), to open them. */
  rootItemIds: z.array(Id).optional(),
  /** Step by step (GET one job only). */
  timeline: z.array(ImportTimelineEntry).optional(),
  /** The last Move to trash / Delete permanently run on what this job created. */
  undo: ImportUndo.optional(),
});
export type ImportJob = z.infer<typeof ImportJob>;

export const ImportResponse = z.object({ import: ImportJob });
export type ImportResponse = z.infer<typeof ImportResponse>;

export const ListImportsResponse = z.object({ imports: z.array(ImportJob) });
export type ListImportsResponse = z.infer<typeof ListImportsResponse>;

/**
 * The workspace's Slack app for personal imports (the "Bundle import" app with Bundle's OAuth redirect URL): set once
 * by an owner or admin. The secret is never returned.
 */
export const SlackAppSettings = z.object({
  configured: z.boolean(),
  clientId: z.string().optional(),
  /** What to put in the Slack app's OAuth & Permissions → Redirect URLs. */
  redirectUrl: z.string(),
});
export type SlackAppSettings = z.infer<typeof SlackAppSettings>;

export const UpdateSlackAppRequest = z.object({
  clientId: z.string().min(5).max(100),
  clientSecret: z.string().min(10).max(200),
});
export type UpdateSlackAppRequest = z.infer<typeof UpdateSlackAppRequest>;

/** Where to send the person to authorise with Slack (personal import). */
export const SlackConnectResponse = z.object({ url: z.string() });
export type SlackConnectResponse = z.infer<typeof SlackConnectResponse>;

/**
 * Ongoing Slack sync (Settings → Import → Slack; owners and admins): while a team moves, a workspace that imported from
 * Slack keeps pulling new Slack activity in about every hour — new messages and thread replies, new channels, edits,
 * deletes and reactions from the last day, and files. One way: nothing written in Bundle goes to Slack. It reads with a
 * Slack user token (the import's, kept when a token import finishes; or one pasted here). "Finish move" stops it and
 * forgets the token.
 */
export const SlackSyncStatus = z.enum(["idle", "running", "ok", "failed"]);
export type SlackSyncStatus = z.infer<typeof SlackSyncStatus>;

export const SlackSyncSettings = z.object({
  /** This workspace has a finished Slack import to keep in sync (else nothing to show). */
  available: z.boolean(),
  /** The hourly sync is on. */
  enabled: z.boolean(),
  /** A Slack token is kept for it (else turning it on needs one). */
  hasToken: z.boolean(),
  /** The Slack workspace it reads. */
  label: z.string().optional(),
  status: SlackSyncStatus,
  /** The last run: when it started and ended, and the messages it brought (new ones, replies included). */
  lastStartedAt: Timestamp.optional(),
  lastFinishedAt: Timestamp.optional(),
  lastMessages: z.number().int().nonnegative().optional(),
  /** `failed`: why. */
  lastError: z.string().optional(),
});
export type SlackSyncSettings = z.infer<typeof SlackSyncSettings>;

/** Turn the hourly sync on or off (paused: the token stays); `token` to set or replace the Slack user token. */
export const UpdateSlackSyncRequest = z.object({
  enabled: z.boolean(),
  token: z.string().min(10).max(500).optional(),
});
export type UpdateSlackSyncRequest = z.infer<typeof UpdateSlackSyncRequest>;

export const ImportTrashResponse = z.object({ trash: ImportTrashCounts });
export type ImportTrashResponse = z.infer<typeof ImportTrashResponse>;

/** POST trashImport / deleteImport: the run (202; already running → the same run, nothing new starts). */
export const ImportUndoResponse = z.object({ undo: ImportUndo });
export type ImportUndoResponse = z.infer<typeof ImportUndoResponse>;

/**
 * Slack app manifest for token imports: create the app from it in the Slack workspace (api.slack.com/apps → From a
 * manifest), install it, and paste the User OAuth Token. Read-only scopes.
 */
export const SLACK_IMPORT_MANIFEST = {
  display_information: { name: "Bundle import", description: "Reads your conversations once to copy them into Bundle." },
  oauth_config: {
    scopes: {
      user: [
        "channels:history", "channels:read", "groups:history", "groups:read", "im:history", "im:read",
        "mpim:history", "mpim:read", "users:read", "users:read.email", "files:read", "team:read",
      ],
    },
  },
  settings: { org_deploy_enabled: false, socket_mode_enabled: false, token_rotation_enabled: false },
} as const;
